import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TeamService } from 'services/team.service';
import { ContainerScrollEventService } from 'services/container-scroll-event.service';
import { ModalService } from 'services/modal.service';
import { PresenceStateService } from 'services/presence-state.service';
import { RecentParticipationService } from 'services/recent-participation.service';
import { redirectAfterCheckout } from 'services/payment.service';
import { RouterService } from 'services/router.service';
import { deleteCookie, getCookie } from 'magma/common/cookies';
import { UserService } from 'services/user.service';
import { filter, take } from 'rxjs/operators';
import { ToastService } from 'magma/services/toast.service';
import { TrackService } from 'services/track.service';
import { Analytics } from 'magma/common/interfaces';
import { UserStore } from 'services/user.store';

@UntilDestroy()
@Component({
  selector: 'manage-page',
  templateUrl: './manage-page.component.pug',
  styleUrls: ['./manage-page.component.scss'],
  host: {
    class: 'use-magma-styles',
    '[class.no-team-selector]': 'noTeamSelector',
  },
  providers: [
    PresenceStateService,
    RecentParticipationService,
  ],
})
export class ManagePageComponent implements AfterViewInit, OnDestroy {
  noTeamSelector = false;
  pageTitle = '';
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private containerScrollEventService: ContainerScrollEventService,
    private modals: ModalService,
    private routerService: RouterService,
    private userService: UserService,
    private toastService: ToastService,
    private trackService: TrackService,
    private userStore: UserStore,
    _teamService: TeamService, // here to ensure teams
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params.paymentUpdated) {
        this.toastService.success({ message: 'Payment method has been updated' });
        void this.router.navigate([], { queryParams: { paymentUpdated: null }, queryParamsHandling: 'merge' });
      }
      if (params.upgraded) {
        this.toastService.success({ message: 'Your artspace was upgraded to Professional Plan' });
        void this.router.navigate([], { queryParams: { upgraded: null }, queryParamsHandling: 'merge' });
      }
    });

    const invite = this.router.getCurrentNavigation()?.extras.state?.invite;
    if (invite) {
      void this.modals.joinTeam({ invite });
    }

    this.routerService.data$.pipe(untilDestroyed(this)).subscribe(data => {
      this.noTeamSelector = data.teamSelector === 'toggle';
      this.pageTitle = data.pageTitle ?? '';
    });

    if (getCookie('open-create-team')) {
      this.userService.user$.pipe(filter(x => !!x), take(1), untilDestroyed(this)).subscribe(() => {
        deleteCookie('open-create-team', '/');
        void this.modals.createTeam();
      });
    }

    window.Intercom?.('update', { hide_default_launcher: false });
  }

  async ngAfterViewInit() {
    const redirect = redirectAfterCheckout();
    if (redirect) return this.router.navigateByUrl(redirect);
  }

  onScrollToEnd() {
    this.containerScrollEventService.approachingBottom();
  }

  // NPS survey

  ngOnDestroy() {
    this.userService.updateUser({ npsSurvey: false });
  }

  private sentNoNPS = false;

  get showSurvey() {
    const show = !!this.userService.user?.npsSurvey;

    if (show && !this.sentNoNPS) {
      this.sentNoNPS = true;
      this.userService.tag('@no-nps').catch(e => DEVELOPMENT && console.error(e));
    }

    return show;
  }

  async openSurvey() {
    this.modals.survey();
    this.userService.updateUser({ npsSurvey: false });
    await this.userService.tag('@no-nps');
  }

  async closeSurveyPopup() {
    this.trackService.event(Analytics.SurveyDismiss);
    this.userService.updateUser({ npsSurvey: false });
    await this.userService.tag('@no-nps');
  }
}
